import { PermissionType, RoleType, roles as rolesConfig, roles } from '@shared/common/types';
import { validateUser } from '@shared/common/data-validators/user.validator';
import { Agency } from '@/modules/agency/agency';

export class User {

    public get roleLabel(): string {
        return (rolesConfig[this.role] && rolesConfig[this.role].label) || 'N/A';
    }

    public static validate(data: any): string[] {
        return validateUser(data);
    }
    public id!: string;
    public active!: boolean;
    public firstname!: string;
    public lastname!: string;
    public email!: string;
    public password!: string;
    public role!: RoleType;

    public devices!: any[];
    public subscription!: any;
    public subscriptionId!: string;
    public createdAt!: Date;
    public deletedAt?: Date;
    public deletedBy?: User;
    public fullname!: string;
    public agencyId!: string;
    public agency!: Agency;
    public lastLogin?: Date;
    public tosAccepted?: boolean;
    public tosAcceptedAt?: Date;
    public phoneNumber?: string;
    public entity?: string;
    public job?: string;

    constructor(opt: any) {
        Object.assign(this, opt);

        if (this.lastLogin) {
            this.lastLogin = new Date(this.lastLogin);
        }

        if (this.deletedBy) {
            this.deletedBy = new User(this.deletedBy);
        }

        if (this.tosAcceptedAt) {
            this.tosAcceptedAt = new Date(this.tosAcceptedAt);
        }
    }

    public can(permissions: PermissionType | PermissionType[]): boolean {
        if (this.isAdmin()) {
            return true;
        }

        if (!Array.isArray(permissions)) {
            permissions = [permissions];
        }

        let userPermissions = rolesConfig[this.role] && rolesConfig[this.role].permissions || [];

        if (Array.isArray(rolesConfig[this.role].extends) && (rolesConfig[this.role].extends || []).length > 0) {
            for (const extendedRole of rolesConfig[this.role].extends as RoleType[]) {
                const extendedPermissions = (rolesConfig[extendedRole] && rolesConfig[extendedRole].permissions || []);
                userPermissions = [...userPermissions, ...extendedPermissions];
            }
        }

        return permissions.every((permission) => userPermissions.includes(permission));
    }

    public canViewRoles(rolesToCheck: RoleType | RoleType[]): boolean {
        rolesToCheck = Array.isArray(rolesToCheck) ? rolesToCheck : [rolesToCheck];
        const canViewUsersWithRoles = roles[this.role].canViewUsersWithRoles;

        if (canViewUsersWithRoles && Array.isArray(canViewUsersWithRoles)) {
            return rolesToCheck.every((roleToCheck) => canViewUsersWithRoles.includes(roleToCheck))
        }

        return true;
    }

    public isAdmin(): boolean {
        return this.isOrExtendsRole(RoleType.ADMIN);
    }

    public isOrExtendsRole(role: RoleType): boolean {
        if (this.role === role) {
            return true;
        }

        return this.extendsRole(role);
    }

    public extendsRole(role: RoleType): boolean {
       return this.doesRoleExtendsOther(this.role, role);
    }

    private doesRoleExtendsOther(baseRole: RoleType, extendedRole: RoleType): boolean {
        const roleConfig = rolesConfig[baseRole];

        if (!roleConfig) {
            return false;
        }

        let directExtendedRoles = roleConfig.extends;

        if (directExtendedRoles) {
            if (!Array.isArray(directExtendedRoles)) {
                directExtendedRoles = [directExtendedRoles];
            }

            if (directExtendedRoles.length > 0) {
                const directlyExtendsRole = directExtendedRoles.some((directExtendedRole) => directExtendedRole === extendedRole);

                if (directlyExtendsRole) {
                    return true;
                }

                return directExtendedRoles.some((directExtendedRole) => this.doesRoleExtendsOther(directExtendedRole, extendedRole));
            }
        }

        return false;
    }

    public is(rolesToCheck: RoleType | RoleType[]): boolean {
        if (this.isAdmin()) {
            return true;
        }

        if (!Array.isArray(rolesToCheck)) {
            rolesToCheck = [rolesToCheck];
        }

        return rolesToCheck.some((role: RoleType) => this.role === role);
    }

    public getSelectableRoles(): RoleType[] {
        return roles[this.role].canCreateUsersWithRoles || [];
    }

    public hasSubscriptionForDevice(device: any): boolean {
        return this.subscription.deviceId === device.id;
    }
}
